import { backgroundOrange, orange } from '@/theme/colors'
import ConfirmationModal from '@components/ConfirmationModal/ConfirmationModal'
import Modal from '@components/Modal'
import { useDeleteTrip, useLeaveTrip } from '@hooks'
import { useUpdateTrip } from '@hooks/useUpdateTrip'
import { ButtonComponent } from '@pages/Itinerary/AddEventModal/FormComponents'
import axios from 'axios'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import TripForm from '../CreateTripModal/TripForm'

interface EditTripModalProps {
	isOpen: boolean
	onClose: () => void
	tripId: string
	initialName: string
	initialCurrencyCode: string
	isOwner?: boolean
}

const EditTripModal: React.FC<EditTripModalProps> = ({
	isOpen,
	onClose,
	tripId,
	initialName,
	initialCurrencyCode,
	isOwner,
}) => {
	const [name, setName] = useState(initialName)
	const [currencyCode, setCurrencyCode] = useState(initialCurrencyCode)
	const updateTrip = useUpdateTrip(tripId)
	const [isConfirmOpen, setIsConfirmOpen] = useState(false)
	const navigate = useNavigate()
	const deleteMutation = useDeleteTrip()
	const leaveMutation = useLeaveTrip()

	// Update local state when initial values change
	useEffect(() => {
		if (isOpen) {
			setName(initialName)
			setCurrencyCode(initialCurrencyCode)
		}
	}, [isOpen, initialName, initialCurrencyCode])

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!name.trim()) {
			return
		}

		try {
			await updateTrip.mutateAsync({
				name: name.trim(),
				defaultCurrencyCode: currencyCode,
			})
			toast.success('Trip settings updated successfully!')
			onClose()
		} catch (error) {
			toast.error('Failed to update trip settings. Please try again.')
			console.error('Error updating trip:', error)
		}
	}

	const handleCancel = () => {
		setName(initialName)
		setCurrencyCode(initialCurrencyCode)
		onClose()
	}

	const handleNameChange = (newName: string) => {
		setName(newName)
	}

	const handleCurrencyChange = (newCurrencyCode: string) => {
		setCurrencyCode(newCurrencyCode)
	}

	const handleDeleteClick = () => {
		setIsConfirmOpen(true)
	}

	const handleConfirmDelete = async () => {
		if (typeof isOwner === 'undefined') {
			// current user not loaded yet — do nothing
			setIsConfirmOpen(false)
			return
		}

		if (!isOwner) {
			leaveMutation.mutate(
				{ tripId },
				{
					onSuccess: () => {
						toast.success('Left trip')
						onClose()
						navigate('/trips')
					},
					onError: error => {
						console.error('Failed to leave trip', error)
						if (axios.isAxiosError(error) && error.response?.status === 441) {
							toast.error('Please delete or remove yourself from all expenses and pay ups first.')
						} else {
							toast.error('Failed to leave trip. Please try again.')
						}
					},
				},
			)
		} else {
			deleteMutation.mutate(
				{ tripId },
				{
					onSuccess: () => {
						toast.success('Trip deleted')
						onClose()
						navigate('/trips')
					},
					onError: error => {
						console.error('Failed to delete trip', error)
						toast.error('Failed to delete trip. Please try again.')
					},
				},
			)
		}

		setIsConfirmOpen(false)
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleCancel}
			title="Trip Settings"
			closeOnBackdropClick={false}
			backgroundColor={backgroundOrange}
			closeButtonColor={orange}
		>
			<TripForm
				name={name}
				currencyCode={currencyCode}
				onNameChange={handleNameChange}
				onCurrencyChange={handleCurrencyChange}
				onSubmit={handleSubmit}
				onCancel={handleCancel}
				submitButtonText="Save Changes"
				submitDisabled={!name.trim() || updateTrip.isPending}
				leftAction={
					typeof isOwner === 'undefined'
						? undefined
						: (
							  <ButtonComponent variant="danger" type="button" onClick={handleDeleteClick}>
								  {!isOwner ? 'Leave trip' : 'Delete trip'}
							  </ButtonComponent>
						  )
				}
			/>

			<ConfirmationModal
				isOpen={isConfirmOpen}
				onClose={() => setIsConfirmOpen(false)}
				onConfirm={handleConfirmDelete}
				title={isOwner === false ? 'Leave Trip' : 'Delete Trip'}
				message={
					isOwner === false
						? 'Are you sure you want to leave this trip? You will no longer have access.'
						: 'Are you sure you want to delete this trip? This action cannot be undone.'
				}
				confirmText={isOwner === false ? 'Leave' : 'Delete'}
				cancelText="Cancel"
				variant="danger"
			/>
		</Modal>
	)
}

export default EditTripModal
